import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 2: RANG
 * Style: Bold Colorful — Vibrant energetic Indian fashion
 * Best for: Contemporary Indian fashion, Festive collections, Bold ethnic wear
 * Near-black theme with striking coral/orange accents
 */
const Template2 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const touchStartX = useRef(0);

    // Categories
    const categories = [
        {
            name: 'Festive Collection',
            description: 'Celebrate every occasion with vibrant hues and intricate craftsmanship.',
            image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=600'
        },
        {
            name: 'Bridal Wear',
            description: 'Timeless bridal ensembles that make your special day unforgettable.',
            image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=600'
        },
        {
            name: 'Daily Elegance',
            description: 'Effortless style for the modern woman who embraces tradition.',
            image: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=600'
        },
        {
            name: 'Fusion Styles',
            description: 'Where contemporary meets heritage in bold, wearable fashion.',
            image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=600'
        }
    ];

    // Products
    const products = [
        { id: 1, name: 'Royal Banarasi Saree', price: '\u20B912,999', badge: 'NEW', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 2, name: 'Silk Embroidered Lehenga', price: '\u20B924,500', image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Designer Anarkali Kurta', price: '\u20B98,999', badge: 'TRENDING', image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Zardozi Bridal Set', price: '\u20B935,000', image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Chanderi Cotton Kurta', price: '\u20B94,999', badge: 'NEW', image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Pashmina Shawl Collection', price: '\u20B96,500', image: 'https://images.pexels.com/photos/8953474/pexels-photo-8953474.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Kanjivaram Silk Saree', price: '\u20B918,999', badge: 'BESTSELLER', image: 'https://images.pexels.com/photos/2955375/pexels-photo-2955375.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 8, name: 'Indo-Western Fusion Gown', price: '\u20B915,499', image: 'https://images.pexels.com/photos/3819793/pexels-photo-3819793.jpeg?auto=compress&cs=tinysrgb&w=400' }
    ];

    // Testimonials
    const testimonials = [
        {
            text: 'The colours are absolutely stunning! My festive lehenga was the talk of the entire celebration. RANG truly understands bold Indian fashion.',
            name: 'Ananya Sharma',
            city: 'Mumbai',
            rating: 5
        },
        {
            text: 'I ordered a bridal set and the craftsmanship left me speechless. Every detail was perfect, from the embroidery to the draping. Highly recommended!',
            name: 'Deepika Patel',
            city: 'Ahmedabad',
            rating: 5
        },
        {
            text: 'Finally a brand that celebrates colour without compromising on elegance. Their fusion collection is perfect for the modern Indian woman.',
            name: 'Riya Verma',
            city: 'Jaipur',
            rating: 5
        }
    ];

    // Auto-rotate testimonials
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    // Touch swipe for testimonials
    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const toggleWishlist = (e, id) => { e.stopPropagation(); setWishlist((prev) => ({ ...prev, [id]: !prev[id] })); };

    return (
        <div style={{ fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif" }} className="w-full min-h-screen">
            {/* Google Fonts */}
            <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />

            {/* ===== SECTION 1: FULL-BLEED HERO ===== */}
            <section className="relative min-h-[70vh] md:min-h-[80vh] lg:min-h-screen w-full overflow-hidden flex items-center justify-center bg-[#0A0A0A]">
                {/* Hero Background Image */}
                <img
                    src="https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=1200"
                    alt="Indian Fashion"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ opacity: 0.5 }}
                />

                {/* Dark Gradient Overlay */}
                <div
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(to bottom, rgba(10,10,10,0.3) 0%, rgba(10,10,10,0.5) 50%, #0A0A0A 100%)'
                    }}
                />

                {/* Hero Content */}
                <div className="relative z-10 text-center px-4 sm:px-6">
                    <p className="text-xs sm:text-sm font-medium tracking-[0.3em] uppercase mb-4 md:mb-6 text-[#FF6B35] font-['Montserrat']">
                        Bold Indian Fashion
                    </p>
                    <h1 className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 md:mb-6 tracking-wider text-white font-['Montserrat'] uppercase">
                        UNLEASH YOUR<br />STYLE
                    </h1>
                    <p className="text-sm sm:text-base lg:text-lg font-light mb-8 md:mb-10 max-w-lg mx-auto text-[#A3A3A3] font-['Montserrat']">
                        Where Vibrant Traditions Meet Modern Expression
                    </p>
                    <button
                        onClick={() => navigate('/template2/shop')}
                        className="inline-flex items-center gap-3 px-8 py-3.5 sm:px-10 sm:py-4 text-sm font-semibold tracking-[0.15em] uppercase transition-all duration-300 bg-[#FF6B35] text-white hover:bg-[#E85D26] font-['Montserrat']"
                    >
                        Explore Collection <ArrowRight className="w-4 h-4" />
                    </button>
                </div>

                {/* Scroll Indicator */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
                    <div className="w-px h-12 opacity-40 bg-[#FF6B35]">
                        <div
                            className="w-full h-1/2 bg-[#FF6B35]"
                            style={{ animation: 'rangScrollPulse 2s ease-in-out infinite' }}
                        />
                    </div>
                </div>
                <style>{`
                    @keyframes rangScrollPulse {
                        0%, 100% { opacity: 0.3; transform: translateY(0); }
                        50% { opacity: 1; transform: translateY(12px); }
                    }
                `}</style>
            </section>

            {/* ===== SECTION 2: CATEGORIES ===== */}
            {/* Mobile: 2-col grid */}
            <section className="lg:hidden py-10 bg-[#0A0A0A]">
                <div className="px-4">
                    <p className="text-xs tracking-[0.3em] uppercase mb-2 text-center text-[#FF6B35] font-['Montserrat'] font-medium">
                        Collections
                    </p>
                    <h2 className="text-2xl font-bold mb-8 text-center text-white font-['Montserrat'] uppercase">
                        Shop by Style
                    </h2>
                    <div className="grid grid-cols-2 gap-3">
                        {categories.map((cat, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template2/shop')}
                                className="relative aspect-[3/4] overflow-hidden cursor-pointer group"
                            >
                                <img
                                    src={cat.image}
                                    alt={cat.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background: 'linear-gradient(to top, rgba(10,10,10,0.9) 0%, rgba(10,10,10,0.3) 40%, transparent 100%)'
                                    }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 p-4">
                                    <h3 className="text-sm font-semibold text-white font-['Montserrat'] uppercase tracking-wide">
                                        {cat.name}
                                    </h3>
                                    <span className="text-xs text-[#FF6B35] font-medium inline-flex items-center gap-1 mt-1 font-['Montserrat']">
                                        Explore <ChevronRight className="w-3 h-3" />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop: 4-col categories */}
            <section className="hidden lg:block py-24 bg-[#0A0A0A]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="text-sm tracking-[0.3em] uppercase mb-3 text-center text-[#FF6B35] font-['Montserrat'] font-medium">
                        Collections
                    </p>
                    <h2 className="text-4xl font-bold mb-16 text-center text-white font-['Montserrat'] uppercase">
                        Shop by Style
                    </h2>
                    <div className="grid grid-cols-4 gap-6">
                        {categories.map((cat, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template2/shop')}
                                className="relative aspect-[3/4] overflow-hidden cursor-pointer group"
                            >
                                <img
                                    src={cat.image}
                                    alt={cat.name}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                />
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background: 'linear-gradient(to top, rgba(10,10,10,0.9) 0%, rgba(10,10,10,0.3) 40%, transparent 100%)'
                                    }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 p-6">
                                    <h3 className="text-lg font-bold text-white font-['Montserrat'] uppercase tracking-wide mb-2">
                                        {cat.name}
                                    </h3>
                                    <p className="text-sm text-[#A3A3A3] font-['Montserrat'] mb-3 line-clamp-2">
                                        {cat.description}
                                    </p>
                                    <span className="text-sm text-[#FF6B35] font-semibold inline-flex items-center gap-2 group-hover:gap-3 transition-all duration-300 font-['Montserrat'] uppercase tracking-wide">
                                        Explore <ArrowRight className="w-4 h-4" />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 3: FEATURED PRODUCTS ===== */}
            {/* Mobile: 2-col grid */}
            <section className="lg:hidden py-10 bg-[#0A0A0A]">
                <div className="px-4">
                    <p className="text-xs tracking-[0.3em] uppercase mb-2 text-center text-[#FF6B35] font-['Montserrat'] font-medium">
                        Handpicked for You
                    </p>
                    <h2 className="text-2xl font-bold mb-8 text-center text-white font-['Montserrat'] uppercase">
                        Featured Products
                    </h2>
                    <div className="grid grid-cols-2 gap-3">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template2/shop')}
                                className="cursor-pointer group bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#FF6B35] transition-all duration-300"
                            >
                                <div className="relative aspect-[3/4] overflow-hidden">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    {product.badge && (
                                        <span className="absolute top-2 left-2 bg-[#FF6B35] text-white text-[10px] font-bold px-2 py-1 uppercase tracking-wider font-['Montserrat']">
                                            {product.badge}
                                        </span>
                                    )}
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-colors bg-[#0A0A0A]/80"
                                    >
                                        <Heart
                                            className="w-4 h-4"
                                            style={{
                                                color: wishlist[product.id] ? '#FF6B35' : '#A3A3A3',
                                                fill: wishlist[product.id] ? '#FF6B35' : 'none'
                                            }}
                                        />
                                    </button>
                                </div>
                                <div className="p-3">
                                    <h3 className="text-xs font-medium text-white line-clamp-1 mb-1 font-['Montserrat']">
                                        {product.name}
                                    </h3>
                                    <p className="text-xs font-bold text-[#FF6B35] font-['Montserrat']">{product.price}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={() => navigate('/template2/shop')}
                        className="w-full mt-6 py-3.5 text-sm font-semibold tracking-[0.15em] uppercase transition-all duration-300 bg-[#FF6B35] text-white hover:bg-[#E85D26] font-['Montserrat']"
                    >
                        View All Products
                    </button>
                </div>
            </section>

            {/* Desktop: 4-col products */}
            <section className="hidden lg:block py-24 bg-[#0A0A0A]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="text-sm tracking-[0.3em] uppercase mb-3 text-center text-[#FF6B35] font-['Montserrat'] font-medium">
                        Handpicked for You
                    </p>
                    <h2 className="text-4xl font-bold mb-16 text-center text-white font-['Montserrat'] uppercase">
                        Featured Products
                    </h2>
                    <div className="grid grid-cols-4 gap-6">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template2/shop')}
                                className="cursor-pointer group bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#FF6B35] transition-all duration-300"
                            >
                                <div className="relative aspect-[3/4] overflow-hidden">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    {product.badge && (
                                        <span className="absolute top-3 left-3 bg-[#FF6B35] text-white text-[11px] font-bold px-3 py-1 uppercase tracking-wider font-['Montserrat']">
                                            {product.badge}
                                        </span>
                                    )}
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 bg-[#0A0A0A]/85"
                                    >
                                        <Heart
                                            className="w-4 h-4"
                                            style={{
                                                color: wishlist[product.id] ? '#FF6B35' : '#A3A3A3',
                                                fill: wishlist[product.id] ? '#FF6B35' : 'none'
                                            }}
                                        />
                                    </button>
                                </div>
                                <div className="p-4">
                                    <h3 className="text-sm font-medium text-white mb-1.5 group-hover:text-[#FF6B35] transition-colors font-['Montserrat']">
                                        {product.name}
                                    </h3>
                                    <p className="text-sm font-bold text-[#FF6B35] font-['Montserrat']">{product.price}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="text-center mt-12">
                        <button
                            onClick={() => navigate('/template2/shop')}
                            className="inline-flex items-center gap-3 px-10 py-4 text-sm font-semibold tracking-[0.15em] uppercase transition-all duration-300 bg-[#FF6B35] text-white hover:bg-[#E85D26] font-['Montserrat']"
                        >
                            View All Products <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: BRAND STORY STRIP ===== */}
            <section className="relative py-16 md:py-24 lg:py-32 overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: "url('https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=1200')"
                    }}
                />
                <div className="absolute inset-0 bg-[#0A0A0A]/85" />

                <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="w-16 h-1 mx-auto mb-8 bg-[#FF6B35]" />
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 text-white font-['Montserrat'] uppercase">
                        The RANG Story
                    </h2>
                    <p className="text-sm sm:text-base leading-relaxed max-w-xl mx-auto mb-6 text-[#A3A3A3] font-['Montserrat']">
                        Born from the vibrant streets of India, RANG celebrates the fearless spirit of colour.
                        We believe fashion should be bold, expressive, and unapologetically alive. Every piece
                        in our collection is a tribute to the rich textile heritage of India, reimagined for
                        the modern world with striking palettes and contemporary silhouettes.
                    </p>
                    <button
                        onClick={() => navigate('/template2/about')}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-[#FF6B35] uppercase tracking-wider hover:gap-3 transition-all duration-300 font-['Montserrat']"
                    >
                        Our Story <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </section>

            {/* ===== SECTION 5: TESTIMONIALS ===== */}
            {/* Mobile: swipeable carousel */}
            <section className="lg:hidden py-10 bg-[#0A0A0A]">
                <div className="px-4">
                    <p className="text-xs tracking-[0.3em] uppercase mb-2 text-center text-[#FF6B35] font-['Montserrat'] font-medium">
                        What Our Customers Say
                    </p>
                    <h2 className="text-2xl font-bold mb-8 text-center text-white font-['Montserrat'] uppercase">
                        Testimonials
                    </h2>
                    <div
                        className="overflow-hidden"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <div
                            className="flex transition-transform duration-500 ease-out"
                            style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}
                        >
                            {testimonials.map((testimonial, index) => (
                                <div key={index} className="w-full flex-shrink-0 px-1">
                                    <div className="p-6 text-center bg-[#1A1A1A] border border-[#2A2A2A]">
                                        <div className="flex justify-center gap-1 mb-4">
                                            {[...Array(testimonial.rating)].map((_, i) => (
                                                <Star key={i} className="w-4 h-4 text-[#FF6B35] fill-[#FF6B35]" />
                                            ))}
                                        </div>
                                        <p className="text-sm leading-relaxed mb-5 italic text-white font-['Montserrat']">
                                            "{testimonial.text}"
                                        </p>
                                        <p className="text-sm font-semibold text-[#FF6B35] font-['Montserrat']">
                                            {testimonial.name}
                                        </p>
                                        <p className="text-xs mt-0.5 text-[#A3A3A3] font-['Montserrat']">
                                            {testimonial.city}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-center gap-2 mt-5">
                            {testimonials.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveTestimonial(i)}
                                    className="h-2 rounded-full transition-all duration-300"
                                    style={{
                                        width: activeTestimonial === i ? '24px' : '8px',
                                        backgroundColor: activeTestimonial === i ? '#FF6B35' : '#2A2A2A'
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Desktop: testimonials */}
            <section className="hidden lg:block py-24 bg-[#0A0A0A]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="text-sm tracking-[0.3em] uppercase mb-3 text-center text-[#FF6B35] font-['Montserrat'] font-medium">
                        What Our Customers Say
                    </p>
                    <h2 className="text-4xl font-bold mb-16 text-center text-white font-['Montserrat'] uppercase">
                        Testimonials
                    </h2>
                    <div className="overflow-hidden" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                        <div
                            className="flex transition-transform duration-500 ease-out"
                            style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}
                        >
                            {testimonials.map((testimonial, index) => (
                                <div key={index} className="w-full flex-shrink-0 px-4">
                                    <div className="max-w-2xl mx-auto p-12 text-center bg-[#1A1A1A] border border-[#2A2A2A]">
                                        <div className="flex justify-center gap-1.5 mb-6">
                                            {[...Array(testimonial.rating)].map((_, i) => (
                                                <Star key={i} className="w-5 h-5 text-[#FF6B35] fill-[#FF6B35]" />
                                            ))}
                                        </div>
                                        <p className="text-xl lg:text-2xl leading-relaxed mb-8 italic font-light text-white font-['Montserrat']">
                                            "{testimonial.text}"
                                        </p>
                                        <p className="text-sm font-semibold tracking-wider text-[#FF6B35] font-['Montserrat']">
                                            {testimonial.name}
                                        </p>
                                        <p className="text-sm mt-1 text-[#A3A3A3] font-['Montserrat']">
                                            {testimonial.city}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-center gap-2 mt-8">
                            {testimonials.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveTestimonial(i)}
                                    className="h-2.5 rounded-full transition-all duration-300"
                                    style={{
                                        width: activeTestimonial === i ? '32px' : '10px',
                                        backgroundColor: activeTestimonial === i ? '#FF6B35' : '#2A2A2A'
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 6: NEWSLETTER ===== */}
            <section className="py-12 md:py-20 bg-[#1A1A1A] border-t border-b border-[#2A2A2A]">
                <div className="max-w-xl mx-auto px-4 text-center">
                    <h2 className="text-xl md:text-2xl font-bold mb-3 text-white font-['Montserrat'] uppercase">
                        Stay in the Loop
                    </h2>
                    <p className="text-sm text-[#A3A3A3] mb-6 font-['Montserrat']">
                        Subscribe for exclusive drops, festive offers, and bold style inspiration.
                    </p>
                    <div className="flex gap-2">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-4 py-3 text-sm bg-[#0A0A0A] border border-[#2A2A2A] text-white placeholder-[#525252] focus:outline-none focus:border-[#FF6B35] transition-colors font-['Montserrat']"
                        />
                        <button className="px-6 py-3 text-sm font-semibold uppercase bg-[#FF6B35] text-white hover:bg-[#E85D26] transition-colors font-['Montserrat']">
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Template2;
